import { Link } from 'react-router-dom'
import { AlertTriangle, AlertCircle, Info, CheckCircle2, ArrowRight } from 'lucide-react'
import Card from '@/components/Card'
import { cn } from '@/lib/utils'
import {
  useDashboardRecommendations,
  type Recommendation,
  type RecommendationSeverity,
} from '@/api/recommendations'

// v29 Phase C — Dashboard-Recommendations-Panel
// Ersetzt die 3 statischen Aktions-Boxen durch eine dynamische Liste, gespeist
// vom /recommendations/dashboard/-Endpoint.

const SEVERITY_TONE: Record<RecommendationSeverity, {
  accent: string
  iconBg: string
  iconFg: string
  Icon: typeof AlertTriangle
}> = {
  high: { accent: 'border-l-red-500', iconBg: 'bg-red-50', iconFg: 'text-red-700', Icon: AlertTriangle },
  medium: { accent: 'border-l-amber-500', iconBg: 'bg-amber-50', iconFg: 'text-amber-700', Icon: AlertCircle },
  low: { accent: 'border-l-blue-500', iconBg: 'bg-blue-50', iconFg: 'text-blue-700', Icon: Info },
}

function RecommendationCard({ rec }: { rec: Recommendation }) {
  const t = SEVERITY_TONE[rec.severity]
  const Icon = t.Icon
  return (
    <Card className={cn('border-l-[3px] p-4', t.accent)}>
      <div className="flex items-start gap-2.5">
        <div className={cn('flex h-7 w-7 items-center justify-center rounded-lg', t.iconBg, t.iconFg)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-[12.5px] font-bold text-[var(--foreground)]">{rec.title}</h3>
          <p className="my-1 text-xs leading-relaxed text-[var(--muted-foreground)]">{rec.body}</p>
          <Link
            to={rec.actionLink}
            className="inline-flex items-center gap-1 rounded-md bg-zinc-900 px-2.5 py-1 text-[11.5px] font-semibold text-white transition-colors hover:bg-zinc-800"
          >
            {rec.actionLabel}
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </Card>
  )
}

function SkeletonCard() {
  return (
    <Card className="border-l-[3px] border-l-[var(--border)] p-4">
      <div className="flex animate-pulse items-start gap-2.5">
        <div className="h-7 w-7 rounded-lg bg-[var(--muted)]" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 w-2/3 rounded bg-[var(--muted)]" />
          <div className="h-3 w-full rounded bg-[var(--muted)]" />
          <div className="h-6 w-24 rounded bg-[var(--muted)]" />
        </div>
      </div>
    </Card>
  )
}

function EmptyState() {
  return (
    <Card className="p-4 text-center">
      <CheckCircle2 className="mx-auto mb-2 h-6 w-6 text-emerald-600" />
      <p className="text-[12.5px] font-bold text-[var(--foreground)]">Alles im gruenen Bereich</p>
      <p className="mt-1 text-xs text-[var(--muted-foreground)]">
        Keine offenen Empfehlungen. Wir benachrichtigen dich, wenn sich das aendert.
      </p>
    </Card>
  )
}

export default function RecommendationsPanel() {
  const { data, isLoading, isError } = useDashboardRecommendations()

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    )
  }

  if (isError || !data) {
    // Bei Fehler: Empty-State (keine fallback-Stub-Boxen mehr — Backend ist source of truth)
    return <EmptyState />
  }

  if (data.recommendations.length === 0) {
    return <EmptyState />
  }

  return (
    <div className="flex flex-col gap-3">
      {data.recommendations.map((rec) => (
        <RecommendationCard key={rec.id} rec={rec} />
      ))}
    </div>
  )
}
